import React from 'react';
import { reportClientError } from '../services/errorReporter';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/* Root-level error boundary: an uncaught render/lifecycle error anywhere in
 * the tree used to unmount the entire app to a blank white page with no way
 * back but a new tab. This catches it and offers a reload instead.
 *
 * Mounted OUTSIDE every context provider (index.tsx), so the fallback must
 * not consume theme/toast/permission contexts — plain Tailwind classes only. */
export default class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // The console line is still what support asks users to screenshot (F12);
    // reportClientError additionally lands it in system_logs (category
    // FRONTEND) so crashes are diagnosable without user cooperation.
    console.error('AppErrorBoundary caught a render error:', error, info.componentStack);
    reportClientError({
      message: `${error.name}: ${error.message}`,
      stack: `${error.stack ?? ''}${info.componentStack ? `\n\nComponent stack:${info.componentStack}` : ''}`,
      source: 'error-boundary',
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
          <div className="text-5xl mb-4" aria-hidden="true">
            ⚠️
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-1">
            The page hit an unexpected error and couldn&apos;t recover on its own.
          </p>
          <p className="text-xs text-gray-400 mb-6 break-words">{this.state.error.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 rounded-md bg-[#0073ae] text-white text-sm font-medium hover:opacity-90"
          >
            Reload page
          </button>
          <p className="text-xs text-gray-400 mt-6">
            If this keeps happening, a browser extension may be interfering with the page — try
            disabling extensions or an InPrivate window, and send IT a screenshot of the browser
            console (F12).
          </p>
        </div>
      </div>
    );
  }
}
